import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CreditCard, DollarSign, ShoppingBag, CheckCircle2, Receipt, Download,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import toast from 'react-hot-toast';
import { getAllOrders, getEveryOrder, getPaymentStats } from '../../api/payments';
import { getRevenueStats } from '../../api/stats';
import { isAccountAdmin } from '../../utils/scope';
import { useAuth } from '../../context/AuthContext';
import useMyCourseIds from '../../hooks/useMyCourseIds';
import PageHead from '../../components/PageHead/PageHead';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import MetricCard from '../../components/MetricCard/MetricCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import SubTabs from '../../components/SubTabs/SubTabs';
import Dropdown from '../../components/Dropdown/Dropdown';
import Pagination from '../../components/Pagination/Pagination';
import InlineEmpty from '../../components/InlineEmpty/InlineEmpty';
import SearchInput from '../../components/SearchInput/SearchInput';
import Avatar from '../../components/Avatar/Avatar';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import SortableTh from '../../components/TableCard/SortableTh';
import {
  ChartFrame, ChartTooltip, AreaGradient, areaProps, seriesColor, CHART_MARGIN, GRID_PROPS,
  X_AXIS_PROPS, Y_AXIS_PROPS, CURSOR_PROPS, compactTick, dateTick,
} from '../../components/charts/chartTheme';
import {
  formatMoney, formatMoneyCompact, formatNumber, formatDate, formatDateTime, todayYmd,
} from '../../utils/datetime';
import { fillDays, niceTicks } from '../../utils/dailySeries';
import { useI18n } from '../../i18n/I18nContext';
import { useScrollFade } from '../../hooks/useScrollFade';
import { statusLabel as wordForStatus } from '../../utils/labels';
import './OrderLists.css';

/* ── Helpers ───────────────────────────────────────────────────────── */

const STATUS_ORDER = ['completed', 'paid', 'processing', 'pending', 'refunded', 'failed', 'cancelled'];
// Status -> the soft tone of its badge, and the data tone of its breakdown bar.
const STATUS_TONE = {
  completed: 'success', paid: 'success', processing: 'info', pending: 'warning',
  refunded: 'neutral', failed: 'danger', cancelled: 'danger',
};

const PAGE_SIZE = 20;
// Sort column -> the API's sortBy
const SORT_FIELD = { date: 'createdAt', amount: 'amount', status: 'status', customer: 'customer' };
const PERIODS = ['7d', '30d', '90d', '1y'];
// The phone has no table head to click, so it gets this list instead.
const SORT_CHOICES = [
  { value: 'date:desc', key: 'sortDateDesc' },
  { value: 'date:asc', key: 'sortDateAsc' },
  { value: 'amount:desc', key: 'sortAmountDesc' },
  { value: 'amount:asc', key: 'sortAmountAsc' },
  { value: 'customer:asc', key: 'sortCustomerAsc' },
];

const toRow = (o) => ({
  ...o,
  _status: String(o.status || '').toLowerCase(),
  _name: o.user?.name || o.userId || '—',
  _course: o.course?.title || o.courseId || '—',
  _amount: Number(o.amount) || 0,
  _currency: o.currency || 'UZS',
  _email: o.user?.email || '',
});

// A spreadsheet runs any cell that starts with = + - @ as a formula: a customer called
// "=HYPERLINK(...)" must stay text. Numbers are passed as numbers and are left alone.
function csvCell(v) {
  let s = String(v == null ? '' : v);
  if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// "25-sentabr, 16:58" may wrap after the comma, never inside the day ("25-" / "sentabr,")
function OrderDate({ value, lang }) {
  if (!value) return '—';
  const text = formatDateTime(value, { lang });
  const at = text.lastIndexOf(', ');
  if (at < 0) return text;
  return (
    <>
      <span className="ol-nowrap">{text.slice(0, at + 1)}</span>
      {' '}
      <span className="ol-nowrap">{text.slice(at + 2)}</span>
    </>
  );
}

/* ── Component ─────────────────────────────────────────────────────── */

function OrderLists() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { courseIds, loading: scopeLoading } = useMyCourseIds();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pageMeta, setPageMeta] = useState({ total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null); // null | 'forbidden' | 'failed'
  const [ordersFailed, setOrdersFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });
  const [period, setPeriod] = useState('30d');
  const [revenueRaw, setRevenueRaw] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesFailed, setSeriesFailed] = useState(false);
  const [seriesKey, setSeriesKey] = useState(0);

  // Account admins see the whole tenant (the server scopes it); everyone else only the courses
  // they own. An owner with no courses has nothing to load.
  const ownCourseIds = useMemo(
    () => (isAccountAdmin(user) ? null : [...(courseIds || [])].join(',')),
    [user, courseIds],
  );
  const courseScope = useMemo(
    () => (ownCourseIds === null ? {} : { courseIds: ownCourseIds }),
    [ownCourseIds],
  );
  const hasNoCourses = ownCourseIds === '';

  // Totals, top courses and the status breakdown come from the server, over every order in scope.
  useEffect(() => {
    let cancelled = false;
    if (scopeLoading) return undefined;
    if (hasNoCourses) {
      setSummary(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getPaymentStats(courseScope)
      .then((res) => { if (!cancelled) setSummary(res); })
      .catch((err) => { if (!cancelled) setError(err?.code === 403 ? 'forbidden' : 'failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseScope, hasNoCourses, scopeLoading, reloadKey]);

  // The search box waits for a pause in typing, then starts again from page 1.
  // Only a real change of the text resets the page, so paging right after load is kept.
  useEffect(() => {
    const next = search.trim();
    if (next === query) return undefined;
    const id = setTimeout(() => {
      setQuery(next);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [search, query]);

  // The orders table: one server page at a time, filtered and sorted by the server.
  const listParams = useMemo(() => ({
    ...courseScope,
    ...(filter !== 'all' ? { status: filter.toUpperCase() } : {}),
    ...(query ? { search: query } : {}),
    sortBy: SORT_FIELD[sort.key],
    sortOrder: sort.dir,
  }), [courseScope, filter, query, sort]);

  useEffect(() => {
    let cancelled = false;
    if (scopeLoading || hasNoCourses) {
      setOrders([]);
      setPageMeta({ total: 0, totalPages: 1 });
      return undefined;
    }
    setOrdersLoading(true);
    setOrdersFailed(false);
    getAllOrders({ ...listParams, page, limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setOrders(res?.data || []);
        setPageMeta({ total: res?.meta?.total ?? 0, totalPages: res?.meta?.totalPages || 1 });
      })
      .catch((err) => {
        if (cancelled) return;
        setOrdersFailed(true);
        if (err?.code === 403) setError('forbidden');
      })
      .finally(() => { if (!cancelled) setOrdersLoading(false); });
    return () => { cancelled = true; };
  }, [listParams, page, hasNoCourses, scopeLoading, reloadKey]);

  // Revenue trend: refetched when the period changes. The server scopes this endpoint to the caller
  // (everything for an admin, the courses a teacher or mentor owns), so it adds up to the tiles.
  useEffect(() => {
    let cancelled = false;
    setSeriesLoading(true);
    setSeriesFailed(false);
    getRevenueStats({ period })
      .then((raw) => { if (!cancelled) setRevenueRaw(raw); })
      // A failed request is not "no revenue": the card says it could not load, and offers a retry
      .catch(() => { if (!cancelled) { setRevenueRaw(null); setSeriesFailed(true); } })
      .finally(() => { if (!cancelled) setSeriesLoading(false); });
    return () => { cancelled = true; };
  }, [period, seriesKey]);

  // The API sends only the days that had a sale; the chart wants every day of the period.
  const series = useMemo(() => fillDays(
    (revenueRaw?.revenueByDay || []).map((day) => ({ date: day.date, value: Number(day.amount ?? day.revenue) || 0 })),
    period,
    'value',
  ), [revenueRaw, period]);
  const periodRevenue = useMemo(() => series.reduce((sum, p) => sum + p.value, 0), [series]);
  // 0, 500 ming, 1 mln, 1.5 mln ... instead of the library's 0, 650 ming, 1.3 mln, 1.95 mln
  const axis = useMemo(() => niceTicks(Math.max(0, ...series.map((p) => p.value))), [series]);

  const rows = useMemo(() => orders.map(toRow), [orders]);
  const scroller = useScrollFade({ watch: `${rows.length}-${loading}-${error}` });

  const currency = rows[0]?._currency || 'UZS';
  const money = useCallback(
    (amount, cur = currency) => formatMoney(amount, cur, { lang, free: false }),
    [currency, lang],
  );
  const moneyCompact = useCallback(
    (amount, cur = currency) => formatMoneyCompact(amount, cur, { lang }),
    [currency, lang],
  );

  const stats = useMemo(() => {
    const total = summary?.totalOrders || 0;
    const completed = summary?.completedOrders || 0;
    return {
      revenue: Number(summary?.revenue) || 0,
      orders: total,
      completed,
      avg: Number(summary?.averageOrderValue) || 0,
      paidRate: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [summary]);

  // Top courses by completed revenue.
  const topCourses = useMemo(() => {
    const list = (summary?.topCourses || []).map((c) => ({
      id: c.courseId,
      course: c.title || c.courseId || '—',
      revenue: Number(c.revenue) || 0,
      count: c.orders,
    }));
    const max = list[0]?.revenue || 1;
    return list.map((c) => ({ ...c, pct: Math.round((c.revenue / max) * 100) }));
  }, [summary]);

  // Status breakdown across all orders.
  const breakdown = useMemo(() => (summary?.statusBreakdown || [])
    .map((g) => ({ status: String(g.status || '').toLowerCase(), count: g.count }))
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
    .map((g) => ({ ...g, pct: stats.orders ? Math.round((g.count / stats.orders) * 100) : 0 })),
  [summary, stats.orders]);

  // The label of a status, in the user's language (the shared status words). Never the raw enum.
  const statusLabel = useCallback((s) => wordForStatus(t, s), [t]);

  // Status filter chips: only the statuses present in the data.
  const statusChips = useMemo(() => [
    { key: 'all', label: t('common.all'), count: stats.orders },
    ...breakdown.map((b) => ({ key: b.status, label: statusLabel(b.status), count: b.count })),
  ], [breakdown, stats.orders, statusLabel, t]);

  const changeFilter = (key) => {
    setFilter(key);
    setPage(1);
  };

  // A head click and the phone's sort list both land here with { key, dir }.
  const applySort = (next) => {
    setSort(next);
    setPage(1);
  };
  const pickSort = (value) => {
    const [key, dir] = value.split(':');
    applySort({ key, dir });
  };

  const clearFilters = () => {
    setSearch('');
    setQuery('');
    setFilter('all');
    setPage(1);
  };
  const filtered = filter !== 'all' || query !== '';

  const periodTabs = useMemo(
    () => PERIODS.map((p) => ({ key: p, label: t(`sales.period${p}`) })),
    [t],
  );
  const sortOptions = useMemo(
    () => SORT_CHOICES.map((o) => ({ value: o.value, label: t(`sales.${o.key}`) })),
    [t],
  );

  const retry = () => setReloadKey((k) => k + 1);

  // Exports every order matching the filters, not only the page on screen. The file keeps ISO
  // dates and raw numbers, whatever language the screen is in.
  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const everyOrder = (await getEveryOrder(listParams)).map(toRow);
      const headers = [
        t('orders.colCustomer'), t('common.email'), t('orders.colCourse'),
        t('orders.colAmount'), t('orders.colStatus'), t('orders.colDate'),
      ];
      const lines = everyOrder.map((o) => [
        o._name, o._email, o._course, o._amount, o._status, o.createdAt || '',
      ].map(csvCell).join(','));
      const csv = [headers.join(','), ...lines].join('\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-${todayYmd()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('sales.exportFailed'));
    } finally {
      setExporting(false);
    }
  }, [listParams, t]);

  const sortValue = `${sort.key}:${sort.dir}`;

  return (
    <div className="ol-page">
      <PageHead
        icon={<CreditCard />}
        title={t('nav.sales')}
        description={t('sales.subtitle')}
        actions={(
          <button
            type="button"
            className={`ds-btn ds-btn-secondary${exporting ? ' is-loading' : ''}`}
            onClick={exportCsv}
            disabled={exporting || pageMeta.total === 0}
          >
            <Download aria-hidden="true" />
            <span>{t('sales.exportCsv')}</span>
          </button>
        )}
      />

      <div className="ui-stack ol-wrap">
      {loading || scopeLoading ? (
        <>
          <div className="kpi-row" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => <MetricCard key={i} loading />)}
          </div>
          {/* The loaded page's own grid, with bones in the cards: nothing jumps when the data lands */}
          <div className="page-grid page-grid--rail ol-grid">
            <div className="ui-stack ol-main">
              <section className="section-card ol-chart-card" aria-hidden="true">
                <div className="ui-skel ol-skel-line ol-skel-title" />
                <div className="ui-skel ol-skel-line ol-skel-sub" />
                <div className="ui-chart ui-skel ol-skel-chart" />
              </section>
              <div className="ol-orders">
                <LoadingState skeleton="table" rows={5} message={t('orders.loading')} />
              </div>
            </div>
            <aside className="col-rail ol-rail" aria-hidden="true">
              {[3, 2].map((rows) => (
                <section key={rows} className="section-card">
                  <div className="ui-skel ol-skel-line ol-skel-title" />
                  <div className="ui-skel ol-skel-line ol-skel-sub" />
                  {Array.from({ length: rows }).map((_, i) => <div key={i} className="ui-skel ol-skel-line ol-skel-row" />)}
                </section>
              ))}
            </aside>
          </div>
        </>
      ) : error ? (
        <div className="ol-error">
          <PageEmptyState
            variant={error === 'forbidden' ? 'forbidden' : 'error'}
            size="lg"
            onRetry={error === 'forbidden' ? undefined : retry}
          />
        </div>
      ) : stats.orders === 0 ? (
        <PageEmptyState
          variant="revenue"
          title={t('orders.emptyTitle')}
          description={t('orders.emptyDesc')}
        />
      ) : (
        <>
          {/* KPI row. Money is compact; the full amount is in the tile's tooltip. */}
          <div className="kpi-row">
            <MetricCard
              title={t('sales.kpiRevenue')}
              value={moneyCompact(stats.revenue)}
              valueTitle={money(stats.revenue)}
              change={t('sales.kpiRevenueSub')}
              icon={<DollarSign size={20} />}
              tone="success"
            />
            <MetricCard
              title={t('orders.ordersCount')}
              value={formatNumber(stats.orders, { lang })}
              change={t('sales.kpiOrdersSub')}
              icon={<ShoppingBag size={20} />}
              tone="info"
            />
            <MetricCard
              title={t('orders.completed')}
              value={formatNumber(stats.completed, { lang })}
              change={`${stats.paidRate}% ${t('sales.paidRate')}`}
              icon={<CheckCircle2 size={20} />}
              tone="brand"
            />
            <MetricCard
              title={t('orders.avgOrder')}
              value={moneyCompact(stats.avg)}
              valueTitle={money(stats.avg)}
              change={t('sales.kpiAvgSub')}
              icon={<Receipt size={20} />}
              tone="purple"
            />
          </div>

          {/* Main column: revenue trend + orders. The rail is one KPI tile wide. */}
          <div className="page-grid page-grid--rail ol-grid">
            <div className="ui-stack ol-main">
            <section className="section-card ol-chart-card">
              <div className="section-head ol-chart-head">
                <div>
                  <h2 className="section-head__title">{t('sales.revenueTrend')}</h2>
                  <p className="section-head__desc" title={series.length ? money(periodRevenue) : undefined}>
                    {series.length
                      ? t('sales.periodTotal', { period: t(`sales.period${period}`), amount: moneyCompact(periodRevenue) })
                      : t('sales.revenueTrendSub')}
                  </p>
                </div>
                <SubTabs
                  className="ol-period"
                  tabs={periodTabs}
                  active={period}
                  onChange={setPeriod}
                  ariaLabel={t('sales.periodAria')}
                />
              </div>
              {seriesLoading ? (
                <div className="ui-chart ui-skel" aria-busy="true" />
              ) : seriesFailed ? (
                <InlineEmpty
                  kind="error"
                  title={t('ui.errorTitle')}
                  description={t('ui.errorDescription')}
                  action={(
                    <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={() => setSeriesKey((k) => k + 1)}>
                      {t('ui.retryAgain')}
                    </button>
                  )}
                />
              ) : (
                <ChartFrame
                  data={series}
                  emptyKind="empty"
                  emptyTitle={t('sales.noRevenueTitle')}
                  emptyDescription={t('sales.noRevenueHint')}
                >
                  <AreaChart data={series} margin={CHART_MARGIN}>
                    <defs><AreaGradient id="olRevenue" color={seriesColor(0)} /></defs>
                    <CartesianGrid {...GRID_PROPS} />
                    <XAxis dataKey="date" {...X_AXIS_PROPS} tickFormatter={dateTick} />
                    <YAxis {...Y_AXIS_PROPS} domain={axis.domain} ticks={axis.ticks} tickFormatter={compactTick} />
                    <Tooltip
                      content={(
                        <ChartTooltip
                          valueFormatter={(v) => money(v)}
                          labelFormatter={(l) => formatDate(l, { lang })}
                        />
                      )}
                      cursor={CURSOR_PROPS}
                    />
                    <Area dataKey="value" name={t('sales.revenueSeriesName')} {...areaProps(0, 'olRevenue')} />
                  </AreaChart>
                </ChartFrame>
              )}
            </section>

            <section className="ui-table-card ol-orders">
              <div className="ol-orders-head">
                <div className="ol-orders-title">
                  <h2 className="section-head__title">{t('sales.ordersHeading')}</h2>
                  <span className="ui-count">{formatNumber(pageMeta.total, { lang })}</span>
                </div>
                <div className="ol-orders-tools">
                  <SearchInput
                    className="ol-search"
                    value={search}
                    onChange={setSearch}
                    placeholder={t('sales.searchPlaceholder')}
                  />
                  <Dropdown
                    value={sortValue}
                    onChange={pickSort}
                    options={sortOptions}
                    placeholder={t('sales.sortLabel')}
                    ariaLabel={t('sales.sortLabel')}
                    className="ol-sort-select"
                  />
                </div>
              </div>

              <div className="ui-chip-row ol-filters" role="group" aria-label={t('sales.statusFilterAria')}>
                {statusChips.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`ui-chip ol-chip${filter === f.key ? ' is-active' : ''}`}
                    aria-pressed={filter === f.key}
                    onClick={() => changeFilter(f.key)}
                  >
                    {f.label}
                    <span className="ui-count">{formatNumber(f.count, { lang })}</span>
                  </button>
                ))}
              </div>

              <div
                ref={scroller.ref}
                className={`ui-table-scroll ol-scroll ${scroller.fade}${ordersLoading ? ' is-loading' : ''}`.replace(/\s+/g, ' ')}
                aria-busy={ordersLoading}
                role="region"
                aria-label={t('sales.ordersHeading')}
                tabIndex={0}
              >
                {rows.length === 0 ? (
                  <div className="ol-empty-wrap">
                    {ordersLoading ? (
                      <LoadingState skeleton="table" rows={4} compact message={t('orders.loading')} />
                    ) : ordersFailed ? (
                      <InlineEmpty
                        kind="error"
                        title={t('ui.errorTitle')}
                        description={t('ui.errorDescription')}
                        action={(
                          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={retry}>
                            {t('ui.retryAgain')}
                          </button>
                        )}
                      />
                    ) : (
                      <InlineEmpty
                        kind="search"
                        title={t('orders.noMatch')}
                        action={filtered ? (
                          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={clearFilters}>
                            {t('ui.filtersReset')}
                          </button>
                        ) : undefined}
                      />
                    )}
                  </div>
                ) : (
                  <table className="ui-table ui-table--stack ol-table">
                    <thead>
                      <tr>
                        <SortableTh sortKey="customer" sort={sort} onSort={applySort}>{t('orders.colCustomer')}</SortableTh>
                        <th scope="col">{t('orders.colCourse')}</th>
                        <SortableTh sortKey="amount" sort={sort} onSort={applySort} numeric defaultDir="desc">{t('orders.colAmount')}</SortableTh>
                        <SortableTh sortKey="status" sort={sort} onSort={applySort}>{t('orders.colStatus')}</SortableTh>
                        <SortableTh sortKey="date" sort={sort} onSort={applySort} defaultDir="desc">{t('orders.colDate')}</SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((o) => (
                        <tr key={o.id}>
                          <td className="ol-col-customer">
                            <div className="ui-cell-person">
                              <Avatar name={o._name === '—' ? '' : o._name} id={o._email || o._name} decorative />
                              <span className="ui-cell-person__text">
                                <span className="ui-cell-person__name" title={o._name}>{o._name}</span>
                                {o._email && <span className="ui-cell-person__sub" title={o._email}>{o._email}</span>}
                              </span>
                            </div>
                          </td>
                          <td className="ol-col-course" data-label={t('orders.colCourse')}>
                            <span className="ol-course" title={o._course}>{o._course}</span>
                          </td>
                          <td className="ui-num ol-col-amount" data-label={t('orders.colAmount')}>
                            <span className="ol-amount">{money(o._amount, o._currency)}</span>
                          </td>
                          <td className="ol-col-status" data-label={t('orders.colStatus')}>
                            <StatusBadge tone={STATUS_TONE[o._status] || 'neutral'}>{statusLabel(o._status)}</StatusBadge>
                          </td>
                          <td className="ol-col-date" data-label={t('orders.colDate')}>
                            <OrderDate value={o.createdAt} lang={lang} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {pageMeta.totalPages > 1 && (
                <div className="ui-table-footer">
                  <Pagination
                    page={page}
                    totalPages={pageMeta.totalPages}
                    total={pageMeta.total}
                    limit={PAGE_SIZE}
                    onPrev={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(pageMeta.totalPages, p + 1))}
                  />
                </div>
              )}
            </section>
            </div>

            <aside className="col-rail ol-rail">
              <section className="section-card">
                <div className="section-head">
                  <div>
                    <h2 className="section-head__title">{t('sales.topCourses')}</h2>
                    <p className="section-head__desc">{t('sales.topCoursesSub')}</p>
                  </div>
                </div>
                {topCourses.length === 0 ? (
                  <InlineEmpty kind="empty" title={t('sales.noCourses')} />
                ) : (
                  <ul className="ol-top">
                    {topCourses.map((c) => (
                      <li key={c.id} className="ol-top-row">
                        <div className="ol-top-line">
                          <span className="ol-top-name" title={c.course}>{c.course}</span>
                          <span className="ol-top-rev">{money(c.revenue)}</span>
                        </div>
                        <ProgressBar value={c.pct} label={c.course} complete={false} />
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="section-card">
                <div className="section-head">
                  <div>
                    <h2 className="section-head__title">{t('sales.statusBreakdown')}</h2>
                    <p className="section-head__desc">{t('sales.statusBreakdownSub')}</p>
                  </div>
                </div>
                <ul className="ol-breakdown">
                  {breakdown.map((b) => (
                    <li key={b.status} className="ol-bd-row">
                      <div className="ol-bd-line">
                        <span className="ol-bd-name">{statusLabel(b.status)}</span>
                        <span className="ol-bd-count">{formatNumber(b.count, { lang })}</span>
                        <span className="ol-bd-pct">{b.pct}%</span>
                      </div>
                      <ProgressBar
                        value={b.pct}
                        label={statusLabel(b.status)}
                        size="sm"
                        tone={STATUS_TONE[b.status] || 'neutral'}
                        complete={false}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            </aside>
          </div>

        </>
      )}
      </div>
    </div>
  );
}

export default OrderLists;
